import { Box, Card, Typography, Skeleton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { CertificatesSummaryData } from '../types'

interface CertificatesSummaryOverviewProps {
  data: CertificatesSummaryData | undefined
  isLoading: boolean
}

interface StatCardProps {
  title: string
  value: string | number
  color: string
}

function StatCard({ title, value, color }: StatCardProps) {
  return (
    <Card
      sx={{
        p: 3,
        backgroundColor: 'background.paperDark',
        border: '1px solid',
        borderColor: 'divider',
        textAlign: 'center',
        position: 'relative',
      }}
    >
      <Typography
        variant="h3"
        sx={{
          color,
          fontWeight: 'bold',
          mb: 1,
          fontSize: '2.5rem',
        }}
      >
        {value}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {title}
      </Typography>
    </Card>
  )
}

export default function CertificatesSummaryOverview({
  data,
  isLoading,
}: CertificatesSummaryOverviewProps) {
  let theme = useTheme()

  if (isLoading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">Learning Achievement Overview</Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              sx={{
                height: 120,
                borderRadius: 1,
              }}
            />
          ))}
        </Box>
      </Box>
    )
  }

  if (!data) {
    return null
  }

  let {
    totalCEHours: totalHours,
    totalActivities,
    totalCertificates,
    averageHoursPerMonth: avgHoursPerMonth,
  } = data.totalStats

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">Learning Achievement Overview</Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        }}
      >
        <StatCard
          title="Total CE Hours"
          value={totalHours}
          color={theme.palette.analyticsColors.ceHours}
        />
        <StatCard
          title="Total Activities"
          value={totalActivities}
          color={theme.palette.analyticsColors.activities}
        />
        <StatCard
          title="Total Certificates"
          value={totalCertificates}
          color={theme.palette.analyticsColors.userMetrics}
        />
        <StatCard
          title="Avg Hours/Month"
          value={avgHoursPerMonth.toFixed(1)}
          color={theme.palette.analyticsColors.ceHours}
        />
      </Box>
    </Box>
  )
}
